import React, { useMemo } from 'react';
import CgTable from 'modules/Common/table';
import { faCalendarDays, faPen, faTrash, faUserShield } from '@fortawesome/free-solid-svg-icons';
import PlanStatusBadge from './PlanStatusBadge';
import { instanceLabel } from '../hooks/useAdminInstances';
import { expiryText, isPlanlessOrgType, planNameText } from '../utils/planDisplay';

/**
 * The canonical Table from modules/Common/table — the barrel's default export is
 * Table.jsx, NOT the frozen modules/Common/table/CgTable (D12).
 *
 * This component owns the column definitions and the row mapping, and receives
 * everything else from useCgTable as `tableProps`. It holds no state: filters,
 * pagination and loading all belong to the hook, so there is exactly one place
 * where "what is the table showing" is decided.
 */

const TYPE_LABELS = {
  declarant: 'Declarant',
  operator: 'Operator',
  consultant: 'Consultant',
  verifier: 'Verifier',
};

const TYPE_TONES = {
  declarant: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  operator: 'bg-sky-50 text-sky-700 ring-sky-200',
  consultant: 'bg-violet-50 text-violet-700 ring-violet-200',
  verifier: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
};

function TypeBadge({ type }) {
  return (
    <span
      data-testid="org-type-badge"
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
        TYPE_TONES[type] ?? 'bg-slate-100 text-slate-600 ring-slate-200'
      }`}
    >
      {TYPE_LABELS[type] ?? type}
    </span>
  );
}

const REGULATION_LABELS = { EU: 'EU CBAM', UK: 'UK CBAM' };

/**
 * Which CBAM regime a declarant is under (CG-2309), beside its name.
 *
 * Declarants only — `regulation` is undefined on operators and consultants, so nothing
 * renders for them. It sits next to the name rather than in its own column because it is a
 * property OF the organization in the way the type badge is: it decides which rules every
 * other number on that row was produced under, and a column would repeat "—" for two thirds
 * of the table.
 *
 * An unrecognised value renders verbatim rather than being dropped — the enum can gain a
 * regime before this build knows about it, and a silently blank badge would read as "no
 * regulation" rather than "newer than me".
 */
function RegulationBadge({ regulation }) {
  if (!regulation) return null;
  return (
    <span
      data-testid="org-regulation"
      className="inline-flex shrink-0 items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200"
    >
      {REGULATION_LABELS[regulation] ?? regulation}
    </span>
  );
}

/**
 * Brand cell.
 *
 * Renders the same label the brand FILTER offers — `instanceLabel`, the brands claiming the
 * instance — and NOT the raw `clerkInstance` host. The two controls describe one thing and
 * used to disagree: the dropdown said `carbonglance / tiba` while its own matching rows said
 * `national-fowl-56.clerk.accounts.dev`, with nothing on screen connecting them. Dev hosts are
 * random-word Clerk subdomains that carry no brand meaning at all, and one instance can serve
 * several brands, so the host both under-describes and reads as noise in a column that exists
 * only to be scanned.
 *
 * An UNRECOGNISED value falls back to the raw host rather than an em dash. A row stamped with
 * an instance the caller does not manage should not normally be reachable — the API scopes the
 * list by exactly that field — so if one appears it is a genuine anomaly, and rendering it
 * blank would make it unfindable.
 */
function BrandCell({ clerkInstance, instances }) {
  const match = instances.find((i) => i.clerkInstance === clerkInstance);
  if (!match) {
    return (
      <span
        className="truncate text-slate-500"
        data-testid="org-brand"
      >
        {clerkInstance || '—'}
      </span>
    );
  }
  return (
    <span
      className="inline-flex min-w-0 items-center gap-1.5"
      data-testid="org-brand"
    >
      <span className="truncate">{instanceLabel(match)}</span>
      {/* Which one is THIS deployment's own. `GET /admin/instances` has always returned
          `isOwn` and the create dialog uses it to preselect a default; the table simply
          never received it. */}
      {match.isOwn ? (
        <span
          data-testid="org-brand-own"
          className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-inset ring-slate-200"
        >
          yours
        </span>
      ) : null}
    </span>
  );
}

/**
 * Column set. `isMultiInstance` adds Brand — on a single-brand deployment every
 * row would repeat the same value, which is a column that costs width and carries
 * no information.
 */
export const buildHeaders = (isMultiInstance) =>
  [
    { label: 'Organization', key: 'organization', noCopyButton: true },
    { label: 'Type', key: 'type', noCopyButton: true },
    isMultiInstance ? { label: 'Brand', key: 'brand' } : null,
    { label: 'Plan', key: 'plan', noCopyButton: true },
    { label: 'Status', key: 'status', noCopyButton: true },
    { label: 'Expires', key: 'expires', noCopyButton: true },
  ].filter(Boolean);

/**
 * Row mapper. Maps 1:1 and never filters, groups or reorders — CgTable indexes
 * `data` by `values`, so a mapper that drops or reorders a row silently pairs one
 * organisation's cells with another's actions (useCgTable D6).
 */
export const buildMapRow =
  (isMultiInstance, instances = []) =>
  (org) => ({
    organization: (
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="truncate font-medium text-slate-900"
            data-testid="org-name"
          >
            {org.name}
          </span>
          <RegulationBadge regulation={org.regulation} />
        </div>
        <div className="truncate text-xs text-slate-500">
          {org.contactEmail || org.clerkOrganisationId || '—'}
        </div>
      </div>
    ),
    type: <TypeBadge type={org.type} />,
    ...(isMultiInstance
      ? {
          brand: (
            <BrandCell
              clerkInstance={org.clerkInstance}
              instances={instances}
            />
          ),
        }
      : {}),
    plan: planNameText(org.type, org.plan),
    status: <PlanStatusBadge plan={org.plan} />,
    expires: expiryText(org.type, org.plan),
  });

export default function OrganizationsTable({
  tableProps,
  onManagePlan,
  onManageFirmAdmins,
  onEdit,
  onDelete,
  onCreate,
}) {
  const actions = useMemo(
    () => [
      {
        label: 'Manage plan',
        icon: faCalendarDays,
        onClick: onManagePlan,
        // Consultants have no plans at all (phase 2). Disabled rather than
        // hidden, with the reason in the tooltip: a verb that vanishes for some
        // rows reads as a rendering bug, one that explains itself does not.
        disabled: (row) => isPlanlessOrgType(row.type),
        title: 'Manage plan',
        ariaLabel: 'Manage plan',
        'data-testid': 'action-manage-plan',
      },
      ...(onManageFirmAdmins
        ? [
            {
              label: 'Firm admins',
              icon: faUserShield,
              onClick: onManageFirmAdmins,
              // Only verifier firms have firm admins (verifier-firm-foundation, R8).
              disabled: (row) => row.type !== 'verifier',
              title: 'Firm admins',
              ariaLabel: 'Firm admins',
              'data-testid': 'action-firm-admins',
            },
          ]
        : []),
      {
        label: 'Edit',
        icon: faPen,
        onClick: onEdit,
        title: 'Edit organization',
        ariaLabel: 'Edit organization',
        'data-testid': 'action-edit',
      },
      {
        label: 'Delete',
        icon: faTrash,
        variant: 'danger',
        onClick: onDelete,
        title: 'Delete organization',
        ariaLabel: 'Delete organization',
        'data-testid': 'action-delete',
      },
    ],
    [onManagePlan, onManageFirmAdmins, onEdit, onDelete],
  );

  return (
    <CgTable
      {...tableProps}
      actions={actions}
      emptyStateTitle="No organizations yet"
      emptyStateDescription="Create the first organization to get started."
      onEmptyStateCreate={onCreate}
      emptyStateCreateLabel="New organization"
    />
  );
}
